import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Business,
  KeyboardArrowDown,
  CheckBox,
  CheckBoxOutlineBlank,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Collapse,
  ListItemButton,
  ListItemText,
  TextField,
  Checkbox,
} from '@mui/material';
import { useConfig } from '../utils/useConfig';

type Producer = {
  id: number;
  name: string;
  logo?: string;
  productCount?: number;
};

type Props = {
  producers: Producer[];
  selectedProducers: number[];
  onProducerChange: (producerId: number, isSelected: boolean) => void;
  isMobile?: boolean;
  onClearAllProducers?: () => void;
};

function Producers({
  producers,
  selectedProducers,
  onProducerChange,
  isMobile = false,
  onClearAllProducers,
}: Props) {
  const config = useConfig();
  const { colors } = config.theme;

  const [openProducersSection, setOpenProducersSection] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredProducers, setFilteredProducers] =
    useState<Producer[]>(producers);

  // Create utility color variables for consistent usage
  const primaryColor = colors.primary;
  const primaryLight = `rgba(${parseInt(primaryColor.slice(1, 3), 16)}, ${parseInt(primaryColor.slice(3, 5), 16)}, ${parseInt(primaryColor.slice(5, 7), 16)}, 0.1)`;
  const primaryLightBorder = `rgba(${parseInt(primaryColor.slice(1, 3), 16)}, ${parseInt(primaryColor.slice(3, 5), 16)}, ${parseInt(primaryColor.slice(5, 7), 16)}, 0.2)`;
  const primaryMedium = `rgba(${parseInt(primaryColor.slice(1, 3), 16)}, ${parseInt(primaryColor.slice(3, 5), 16)}, ${parseInt(primaryColor.slice(5, 7), 16)}, 0.6)`;

  // Filtruj producentów przy zmianie wyszukiwania
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredProducers(producers);
    } else {
      const filtered = producers.filter((producer) =>
        producer.name.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setFilteredProducers(filtered);
    }
  }, [searchQuery, producers]);

  // Dodajmy nowy effect, który zresetuje wyszukiwanie gdy się zmieniają producenci
  useEffect(() => {
    setSearchQuery('');
    setFilteredProducers(producers);
  }, [producers]);

  // Animacje
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: { opacity: 1, x: 0 },
  };

  // Zawartość producenta dla widoku mobilnego (bez nagłówka i obramowania)
  const renderMobileContent = () => {
    return (
      <motion.div variants={containerVariants} className="space-y-3">
        {/* Wyszukiwarka producentów */}
        <motion.div variants={itemVariants}>
          <TextField
            fullWidth
            size="small"
            placeholder="Szukaj producenta..."
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon
                  fontSize="small"
                  className="mr-2"
                  style={{ color: primaryMedium }}
                />
              ),
              className: 'bg-white text-sm',
              sx: {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: primaryLightBorder,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${primaryColor}80`,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: primaryColor,
                },
              },
            }}
          />
        </motion.div>

        {/* Lista producentów */}
        {filteredProducers.length > 0 ? (
          <motion.div
            variants={itemVariants}
            className="max-h-64 overflow-y-auto pr-1 space-y-1"
            style={{ scrollbarWidth: 'thin' }}
          >
            {filteredProducers.map((producer) => (
              <motion.div
                key={producer.id}
                style={{
                  backgroundColor: selectedProducers.includes(producer.id)
                    ? primaryLight
                    : 'transparent',
                }}
                className="flex items-center p-2 rounded-md cursor-pointer"
                onClick={() =>
                  onProducerChange(
                    producer.id,
                    !selectedProducers.includes(producer.id),
                  )
                }
                whileHover={{ scale: 1.01, backgroundColor: primaryLight }}
                whileTap={{ scale: 0.99 }}
              >
                <Checkbox
                  checked={selectedProducers.includes(producer.id)}
                  onChange={() => {}}
                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBox fontSize="small" />}
                  size="small"
                  sx={{
                    color: primaryMedium,
                    '&.Mui-checked': {
                      color: primaryColor,
                    },
                  }}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-800 truncate">
                    {producer.name}
                  </div>
                </div>
                {producer.productCount && (
                  <div className="text-xs text-gray-500 ml-1">
                    ({producer.productCount})
                  </div>
                )}
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            variants={itemVariants}
            className="p-4 text-center bg-white rounded-md"
          >
            <p className="text-gray-500 text-sm">
              Nie znaleziono producentów
              {searchQuery ? ` pasujących do "${searchQuery}"` : ''}
            </p>
          </motion.div>
        )}

        {/* Przyciski akcji - jeśli są wybrani producenci */}
        {selectedProducers.length > 0 && (
          <motion.div
            variants={itemVariants}
            className="flex justify-between pt-2"
            style={{ borderTop: `1px solid ${primaryLightBorder}` }}
          >
            <button
              className="text-xs font-medium hover:opacity-80"
              style={{ color: primaryColor }}
              onClick={() => {
                if (onClearAllProducers) {
                  onClearAllProducers();
                } else {
                  // Fallback w przypadku gdy onClearAllProducers nie jest dostępne
                  selectedProducers.forEach((id) =>
                    onProducerChange(id, false),
                  );
                }
              }}
            >
              Wyczyść wybór
            </button>
            <div
              className="text-xs font-medium"
              style={{ color: primaryColor }}
            >
              Wybrano: {selectedProducers.length}
            </div>
          </motion.div>
        )}
      </motion.div>
    );
  };

  // Zawartość producenta dla widoku desktopowego (z nagłówkiem i obramowaniem)
  const renderDesktopContent = () => {
    return (
      <motion.div
        className="rounded-lg border shadow-sm overflow-hidden"
        style={{
          background: `linear-gradient(to bottom right, ${primaryLight}, white)`,
          borderColor: primaryLightBorder,
        }}
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <div style={{ borderBottom: `1px solid ${primaryLightBorder}` }}>
          <ListItemButton
            onClick={() => setOpenProducersSection(!openProducersSection)}
            className="py-3"
          >
            <motion.div
              variants={itemVariants}
              className="flex items-center mr-2"
              style={{ color: primaryColor }}
            >
              <Business fontSize="small" />
            </motion.div>
            <ListItemText
              primary={
                <motion.span
                  variants={itemVariants}
                  className="font-bold text-gray-800"
                >
                  Producenci
                </motion.span>
              }
            />
            <motion.div
              variants={itemVariants}
              className="text-xs font-medium px-2 py-0.5 rounded-full"
              style={{
                backgroundColor: primaryLight,
                color: primaryColor,
              }}
            >
              {selectedProducers.length > 0
                ? `${selectedProducers.length}`
                : `${producers.length}`}
            </motion.div>
            <KeyboardArrowDown
              className={`transition-transform duration-300 ml-2 ${
                openProducersSection ? 'rotate-180' : ''
              }`}
            />
          </ListItemButton>
        </div>

        <Collapse in={openProducersSection}>
          <div className="p-3 space-y-3">
            {/* Wyszukiwarka producentów */}
            <motion.div variants={itemVariants}>
              <TextField
                fullWidth
                size="small"
                placeholder="Szukaj producenta..."
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchIcon
                      fontSize="small"
                      className="mr-2"
                      style={{ color: primaryMedium }}
                    />
                  ),
                  className: 'bg-white text-sm',
                  sx: {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: primaryLightBorder,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: `${primaryColor}80`,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: primaryColor,
                    },
                  },
                }}
              />
            </motion.div>

            {/* Lista producentów */}
            {filteredProducers.length > 0 ? (
              <motion.div
                variants={itemVariants}
                className="max-h-64 overflow-y-auto pr-1 space-y-1"
                style={{ scrollbarWidth: 'thin' }}
              >
                {filteredProducers.map((producer) => (
                  <motion.div
                    key={producer.id}
                    style={{
                      backgroundColor: selectedProducers.includes(producer.id)
                        ? primaryLight
                        : 'transparent',
                    }}
                    className="flex items-center p-2 rounded-md cursor-pointer"
                    onClick={() =>
                      onProducerChange(
                        producer.id,
                        !selectedProducers.includes(producer.id),
                      )
                    }
                    whileHover={{ scale: 1.01, backgroundColor: primaryLight }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <Checkbox
                      checked={selectedProducers.includes(producer.id)}
                      onChange={() => {}}
                      icon={<CheckBoxOutlineBlank fontSize="small" />}
                      checkedIcon={<CheckBox fontSize="small" />}
                      size="small"
                      sx={{
                        color: primaryMedium,
                        '&.Mui-checked': {
                          color: primaryColor,
                        },
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-800 truncate">
                        {producer.name}
                      </div>
                    </div>
                    {producer.productCount && (
                      <div className="text-xs text-gray-500 ml-1">
                        ({producer.productCount})
                      </div>
                    )}
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <motion.div
                variants={itemVariants}
                className="p-4 text-center bg-white rounded-md"
              >
                <p className="text-gray-500 text-sm">
                  Nie znaleziono producentów
                  {searchQuery ? ` pasujących do "${searchQuery}"` : ''}
                </p>
              </motion.div>
            )}

            {/* Przyciski akcji - jeśli są wybrani producenci */}
            {selectedProducers.length > 0 && (
              <motion.div
                variants={itemVariants}
                className="flex justify-between pt-2"
                style={{ borderTop: `1px solid ${primaryLightBorder}` }}
              >
                <button
                  className="text-xs font-medium hover:opacity-80"
                  style={{ color: primaryColor }}
                  onClick={() => {
                    if (onClearAllProducers) {
                      onClearAllProducers();
                    } else {
                      // Fallback w przypadku gdy onClearAllProducers nie jest dostępne
                      selectedProducers.forEach((id) =>
                        onProducerChange(id, false),
                      );
                    }
                  }}
                >
                  Wyczyść wybór
                </button>
                <div
                  className="text-xs font-medium"
                  style={{ color: primaryColor }}
                >
                  Wybrano: {selectedProducers.length}
                </div>
              </motion.div>
            )}
          </div>
        </Collapse>
      </motion.div>
    );
  };

  return isMobile ? renderMobileContent() : renderDesktopContent();
}

export default Producers;
